import React, { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import { Capacitor } from '@capacitor/core'

import { openModal } from 'store/actions/modals'
import { hasAccessToThis } from 'services/Utilities'
import { getAvailableProjectTransfersForMultiselect } from 'services/getAvailableProjectTransfersForMultiselect'
import { getAvailablePositionTransfersForMultiselect } from 'services/getAvailablePositionTransfersForMultiselect'
import { getAssignablePositions } from 'services/Structure'
import { canEmployeePunch } from 'services/PunchCards'
import { Dropdown } from 'ui'

import { t } from 'translations'

const PunchDropdownOptions = ({ event, punchCard }) => {
  const dispatch = useDispatch()

  const { projectsById } = useSelector(state => state.resources)
  const { enablePositionTransfer, enableProjectTransfer, enablePunchGeolocation } = useSelector(state => state.settings)
  const isNative = Capacitor.isNativePlatform()
  const deviceLocationRequired = isNative && enablePunchGeolocation
  const punchModal = deviceLocationRequired ? 'PunchInOutModalGeolocation' : 'PunchInOutModal'
  const { userInfo } = useSelector(state => state.users)

  const { allowedToPunchIn, allowedToPunchOut } = canEmployeePunch(event)

  // Get all the multiselect options for transfers
  const [positionOptions, projectOptions] = useMemo(() => {
    const projectOptions = getAvailableProjectTransfersForMultiselect(punchCard, event, userInfo, projectsById, getAssignablePositions)
    const positionOptions = getAvailablePositionTransfersForMultiselect(punchCard, event, userInfo, t, getAssignablePositions)
    return [positionOptions, projectOptions]
  }, [punchCard, event, userInfo, projectsById])

  const hasPositionOptions = !isEmpty(positionOptions)
  const displayPositionTransfer = enablePositionTransfer && hasPositionOptions && allowedToPunchOut
  const displayProjectTransfer = enableProjectTransfer && !isEmpty(projectOptions) && allowedToPunchOut

  // Find out if there is a currently punched break on the punch card
  let currentlyPunchedBreak
  if (punchCard && punchCard.breaks) {
    for (const breakObject of punchCard.breaks) {
      const breakStartedButNotEnded = breakObject.start && !breakObject.end && !currentlyPunchedBreak
      currentlyPunchedBreak = breakStartedButNotEnded ? breakObject : currentlyPunchedBreak
    }
  }

  // If there are no options, return here
  if (punchCard === 'notLoaded' || !hasAccessToThis('punchOnShifts') || (punchCard && punchCard.leave)) return null

  const options = []

  // Punch in
  if (!punchCard || !punchCard.arrival) {
    if (allowedToPunchIn) {
      options.push(
        <Dropdown.Item key="punchIn" onClick={() => dispatch(openModal(punchModal, { event, punchingIn: true }))}>
          <i className="fas fa-sign-in-alt mr-2 fa-fw" />
          {t('employee.punch.PUNCH_IN')}
        </Dropdown.Item>
      )
    }
  } else if (!currentlyPunchedBreak) {
    // Punch out
    if (allowedToPunchOut) {
      options.push(
        <Dropdown.Item key="punchOut" onClick={() => dispatch(openModal(punchModal, { event }))}>
          <i className="fas fa-sign-out-alt mr-2 fa-fw" />
          {t('employee.punch.PUNCH_OUT')}
        </Dropdown.Item>
      )

      // Punch out break
      options.push(
        <Dropdown.Item key="breakStart" onClick={() => dispatch(openModal('PunchStartBreakModal', { punchCard, breaks: event.breaks }))}>
          <i className="fas fa-coffee mr-2 fa-fw" />
          {t('employee.punch.START_BREAK')}
        </Dropdown.Item>
      )
    }
    // Punch position transfer
    if (displayPositionTransfer) {
      options.push(
        <Dropdown.Item key="transfer" onClick={() => dispatch(openModal('PunchTransferModal', { punchCard, positionOptions }))}>
          <i className="fas fa-exchange-alt mr-2 fa-fw" />
          {t('employee.punch.TRANSFER')}
        </Dropdown.Item>
      )
    }
    // Punch project transfer
    if (displayProjectTransfer) {
      options.push(
        <Dropdown.Item key="transfer_project" onClick={() => dispatch(openModal('PunchProjectTransferModal', { punchCard, projectOptions }))}>
          <i className="fas fa-exchange-alt mr-2 fa-fw" />
          {t('employee.punch.TRANSFER_PROJECT')}
        </Dropdown.Item>
      )
    }
  } else {
    // Punch in break
    options.push(
      <Dropdown.Item key="breakEnd" onClick={() => dispatch(openModal('PunchEndBreakModal', { punchCard, currentlyPunchedBreak }))}>
        <i className="fas fa-coffee mr-2 fa-fw" />
        {t('employee.punch.END_BREAK')}
      </Dropdown.Item>
    )
  }

  if (options.length === 0) return null

  return (
    <div>
      <Dropdown.Divider />
      <Dropdown.Header>{t('employee.punch.PUNCH_OPTIONS')}</Dropdown.Header>
      {options}
    </div>
  )
}

export default PunchDropdownOptions
